/**
 * /schedule — screen 3c.
 *
 * A week pager over the calendar from `GET /api/weeks`, with the slate for the
 * selected week from `GET /api/games?week=`. Chevrons, keyboard arrows and a
 * horizontal swipe all move the same cursor (D39); "This week" snaps back to the
 * week the group is actually in.
 *
 * Two rules from D39 govern the right-hand column and are easy to get wrong:
 *   - a result chip is relative to the *viewer's* team, so it only appears on
 *     games the viewer's team is in; everyone else's final is just "Final"
 *   - the spread appears only inside 7 days of kickoff, and only when odds
 *     actually exist. It is never a placeholder.
 */

import { useCallback, useEffect, useMemo, useRef, useState, type TouchEvent as ReactTouchEvent } from 'react'
import { Link } from 'react-router-dom'
import type { Game, WeekRef } from '@shared/types'
import { teamAbbr } from '@shared/teams'
import { useStore } from '../lib/store'
import { ApiFailure, GET } from '../lib/api'
import { Body, Eyebrow, Header, Notice, Pill, Screen, SkeletonCard, Spacer, TeamLogo } from '../ui'

const DAY_MS = 86_400_000
const SEVEN_DAYS = 7 * DAY_MS

const WEEKDAY_LONG = new Intl.DateTimeFormat(undefined, { weekday: 'long' })
const MONTH_SHORT = new Intl.DateTimeFormat(undefined, { month: 'short' })
const MONTH_DAY_SHORT = new Intl.DateTimeFormat(undefined, { month: 'short', day: 'numeric' })
const DAY_NUM = new Intl.DateTimeFormat(undefined, { day: 'numeric' })
const HHMM = new Intl.DateTimeFormat(undefined, { hour: 'numeric', minute: '2-digit', hour12: true })

/** "Sep 6–15", or "Sep 30–Oct 6" when the window straddles a month. */
function weekRange(w: WeekRef): string {
  const a = w.startsAt
  const b = w.endsAt
  const sameMonth = MONTH_SHORT.format(a) === MONTH_SHORT.format(b)
  return sameMonth
    ? `${MONTH_DAY_SHORT.format(a)}–${DAY_NUM.format(b)}`
    : `${MONTH_DAY_SHORT.format(a)}–${MONTH_DAY_SHORT.format(b)}`
}

/** "Thursday, Sep 10" — the day-group heading. */
function dayHeading(at: number): string {
  return `${WEEKDAY_LONG.format(at)}, ${MONTH_DAY_SHORT.format(at)}`
}

/** A stable key for "the local day this kickoff belongs to". */
function dayKey(at: number): string {
  const d = new Date(at)
  return `${d.getFullYear()}-${d.getMonth() + 1}-${d.getDate()}`
}

function periodLabel(period: number | null): string {
  if (!period) return ''
  if (period <= 4) return `Q${period}`
  return period === 5 ? 'OT' : `OT${period - 4}`
}

/** The spread reads from the home team's perspective; negative means home. */
function spreadLabel(g: Game): string | null {
  if (g.spread === null) return null
  if (g.spread === 0) return 'PK'
  const favorite = g.spread < 0 ? g.homeTeamId : g.awayTeamId
  return `${teamAbbr(favorite)} −${Math.abs(g.spread)}`
}

interface WeeksResponse {
  season: number
  weeks: WeekRef[]
  currentWeekId: string | null
}

interface GamesResponse {
  week: WeekRef
  games: Game[]
  staleAsOf: number | null
}

export default function Schedule() {
  const { state } = useStore()
  const myTeamId = state?.user.teamId ?? null
  const season = state?.week?.seasonYear ?? new Date().getFullYear()

  const [weeks, setWeeks] = useState<WeekRef[] | null>(null)
  const [currentWeekId, setCurrentWeekId] = useState<string | null>(null)
  const [cursor, setCursor] = useState<string | null>(null)
  const [games, setGames] = useState<Game[] | null>(null)
  const [err, setErr] = useState<string | null>(null)
  const [loadingSlate, setLoadingSlate] = useState(false)

  // ── the calendar ──────────────────────────────────────────────────────────

  useEffect(() => {
    const ac = new AbortController()
    void (async () => {
      try {
        const res = await GET<WeeksResponse>(`/api/weeks?season=${season}`, ac.signal)
        setWeeks(res.weeks)
        setCurrentWeekId(res.currentWeekId)
        setCursor((c) => c ?? res.currentWeekId ?? res.weeks[0]?.id ?? null)
        setErr(null)
      } catch (e) {
        if (ac.signal.aborted) return
        setWeeks((prev) => prev ?? [])
        setErr(e instanceof ApiFailure ? e.message : 'Couldn’t load the calendar.')
      }
    })()
    return () => ac.abort()
  }, [season])

  // ── the slate for the cursor week ─────────────────────────────────────────

  useEffect(() => {
    if (!cursor) return
    const ac = new AbortController()
    setLoadingSlate(true)
    void (async () => {
      try {
        const res = await GET<GamesResponse>(`/api/games?week=${encodeURIComponent(cursor)}`, ac.signal)
        setGames(res.games)
        setErr(null)
      } catch (e) {
        if (ac.signal.aborted) return
        // Keep whatever slate is on screen; the notice explains the staleness.
        setErr(e instanceof ApiFailure ? e.message : 'Couldn’t load that week.')
      } finally {
        if (!ac.signal.aborted) setLoadingSlate(false)
      }
    })()
    return () => ac.abort()
  }, [cursor])

  // ── paging ────────────────────────────────────────────────────────────────

  const index = useMemo(
    () => (weeks && cursor ? weeks.findIndex((w) => w.id === cursor) : -1),
    [weeks, cursor],
  )
  const week = index >= 0 && weeks ? weeks[index] : null
  const canPrev = index > 0
  const canNext = !!weeks && index >= 0 && index < weeks.length - 1

  const step = useCallback((delta: number) => {
    setCursor((c) => {
      if (!weeks || !c) return c
      const i = weeks.findIndex((w) => w.id === c)
      const next = i + delta
      if (i < 0 || next < 0 || next >= weeks.length) return c
      setGames(null)
      return weeks[next].id
    })
  }, [weeks])

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      const target = e.target as HTMLElement | null
      if (target && /^(INPUT|TEXTAREA|SELECT)$/.test(target.tagName)) return
      if (e.key === 'ArrowLeft') { e.preventDefault(); step(-1) }
      if (e.key === 'ArrowRight') { e.preventDefault(); step(1) }
    }
    window.addEventListener('keydown', onKey)
    return () => window.removeEventListener('keydown', onKey)
  }, [step])

  const touch = useRef<{ x: number; y: number } | null>(null)
  const onTouchStart = useCallback((e: ReactTouchEvent<HTMLDivElement>) => {
    const t = e.touches[0]
    touch.current = { x: t.clientX, y: t.clientY }
  }, [])
  const onTouchEnd = useCallback((e: ReactTouchEvent<HTMLDivElement>) => {
    const start = touch.current
    touch.current = null
    if (!start) return
    const t = e.changedTouches[0]
    const dx = t.clientX - start.x
    const dy = t.clientY - start.y
    if (Math.abs(dx) < 60 || Math.abs(dy) > 45) return
    step(dx < 0 ? 1 : -1)
  }, [step])

  // ── grouping ──────────────────────────────────────────────────────────────

  const groups = useMemo(() => {
    if (!games) return []
    const out: { key: string; at: number; games: Game[] }[] = []
    for (const g of [...games].sort((a, b) => a.kickoffAt - b.kickoffAt)) {
      const key = dayKey(g.kickoffAt)
      const last = out[out.length - 1]
      if (last && last.key === key) last.games.push(g)
      else out.push({ key, at: g.kickoffAt, games: [g] })
    }
    return out
  }, [games])

  const now = Date.now()

  return (
    <Screen style={{ flex: '1 1 auto', minHeight: 0 }}>
      <Header
        left={
          <div style={{ display: 'flex', alignItems: 'center', gap: 6, minWidth: 0 }}>
            <button
              onClick={() => step(-1)}
              disabled={!canPrev}
              aria-label="Previous week"
              style={{ color: canPrev ? 'var(--dim)' : 'var(--line)', minWidth: 36, minHeight: 44, textAlign: 'left', flex: '0 0 auto' }}
            >
              ◀
            </button>
            <span
              style={{
                fontSize: 15, fontWeight: 700, minWidth: 0,
                overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
              }}
              aria-live="polite"
            >
              {week ? `${week.label} · ${weekRange(week)}` : 'Schedule'}
            </span>
            <button
              onClick={() => step(1)}
              disabled={!canNext}
              aria-label="Next week"
              style={{ color: canNext ? 'var(--dim)' : 'var(--line)', minWidth: 36, minHeight: 44, textAlign: 'right', flex: '0 0 auto' }}
            >
              ▶
            </button>
          </div>
        }
        right={
          <Pill
            onClick={() => { if (currentWeekId) { setGames(null); setCursor(currentWeekId) } }}
            style={{ color: 'var(--bright)', padding: '5px 12px', minHeight: 44, whiteSpace: 'nowrap' }}
          >
            This week
          </Pill>
        }
      />

      <Body style={{ padding: '16px var(--gutter) calc(24px + var(--nav-h))' }}>
        {/* The swipe target is the whole slate area, not just the rows. */}
        <div onTouchStart={onTouchStart} onTouchEnd={onTouchEnd} style={{ minHeight: '60dvh' }}>
        {err ? <><Notice tone="warn">{err}</Notice><Spacer h={16} /></> : null}

        {games === null && loadingSlate ? (
          <>
            <SkeletonCard lines={4} />
            <Spacer h={12} />
            <SkeletonCard lines={4} />
          </>
        ) : groups.length === 0 ? (
          <div style={{ marginTop: 8 }}>
            <Eyebrow>{week ? week.label : 'This week'}</Eyebrow>
            <div style={{ fontSize: 15, fontWeight: 600, marginTop: 12 }}>No games this week.</div>
            <div style={{ fontSize: 13, color: 'var(--dim)', marginTop: 6 }}>
              Swipe or use the arrows to look at another week.
            </div>
          </div>
        ) : (
          groups.map((group, gi) => (
            <div key={group.key}>
              <Eyebrow style={{ marginTop: gi === 0 ? 0 : 28 }}>{dayHeading(group.at)}</Eyebrow>
              {group.games.map((g) => (
                <GameRow key={g.id} game={g} myTeamId={myTeamId} now={now} />
              ))}
            </div>
          ))
        )}
        </div>
      </Body>
    </Screen>
  )
}

function GameRow({ game, myTeamId, now }: { game: Game; myTeamId: string | null; now: number }) {
  const away = teamAbbr(game.awayTeamId)
  const home = teamAbbr(game.homeTeamId)
  const isMine = !!myTeamId && (game.homeTeamId === myTeamId || game.awayTeamId === myTeamId)

  const scored = game.homeScore !== null && game.awayScore !== null

  let right = ''
  let rightColor = 'var(--dim)'

  if (game.state === 'post') {
    const base = game.wentToOt ? 'Final/OT' : 'Final'
    if (isMine && scored) {
      const mineHome = game.homeTeamId === myTeamId
      const mineScore = mineHome ? game.homeScore! : game.awayScore!
      const theirScore = mineHome ? game.awayScore! : game.homeScore!
      const mark = mineScore > theirScore ? 'W' : mineScore < theirScore ? 'L' : 'T'
      right = `${base} · ${mark}`
      rightColor = 'var(--bright)'
    } else {
      right = base
    }
  } else if (game.state === 'in') {
    right = [periodLabel(game.period), game.clockDisplay].filter(Boolean).join(' ')
      || game.statusDetail || 'Live'
    rightColor = 'var(--fg)'
  } else {
    const time = HHMM.format(game.kickoffAt)
    const spread = game.kickoffAt - now <= SEVEN_DAYS ? spreadLabel(game) : null
    right = spread ? `${spread} · ${time}` : time
  }

  return (
    <Link
      to={`/game/${game.id}`}
      style={{
        display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 12,
        padding: '14px 2px', borderBottom: '1px solid var(--line)',
        color: 'var(--fg)', minHeight: 64,
      }}
    >
      <div style={{ display: 'flex', alignItems: 'center', gap: 12, minWidth: 0, flex: '1 1 auto' }}>
        <div style={{ minWidth: 0 }}>
          {/*
            Each badge sits against its own abbreviation rather than both of
            them bunched to the left of the text. Two logos in a row, then two
            names in a row, made you pair them up yourself — and on the eleven
            games a week where neither is yours, nobody bothers.
          */}
          <div
            className="mono"
            style={{
              display: 'flex', alignItems: 'center', gap: 6,
              fontSize: 14, fontWeight: isMine ? 700 : 500,
            }}
          >
            {isMine ? <span aria-hidden>★</span> : null}
            <TeamLogo teamId={game.awayTeamId} size={24} fallback="none" />
            <span>{away}</span>
            <span style={{ color: 'var(--dim)', margin: '0 2px' }}>@</span>
            <TeamLogo teamId={game.homeTeamId} size={24} fallback="none" />
            <span>{home}</span>
          </div>
          {scored && game.state !== 'pre' ? (
            <div className="mono" style={{ fontSize: 13, color: 'var(--dim)', marginTop: 4 }}>
              {game.awayScore} - {game.homeScore}
            </div>
          ) : null}
        </div>
      </div>
      <span className="mono" style={{ fontSize: 12, color: rightColor, flex: '0 0 auto', textAlign: 'right' }}>
        {right}
      </span>
    </Link>
  )
}
